import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

export default function CustomerPrices() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [prices, setPrices] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ product_id: '', sale_price: '', currency: 'USD' });

  useEffect(() => {
    const fetchData = async () => {
      const [c, p, cp] = await Promise.all([
        api.get(`/api/customers/${id}`),
        api.get('/api/products/'),
        api.get(`/api/customer-prices/customer/${id}`)
      ]);
      setCustomer(c.data);
      setProducts(p.data);
      setPrices(cp.data);
    };
    fetchData();
  }, [id]);

  const fetchPrices = async () => {
    const res = await api.get(`/api/customer-prices/customer/${id}`);
    setPrices(res.data);
  };

  const getProductName = (productId) => {
    const p = products.find(p => p.id === productId);
    return p ? `${p.code} - ${p.name}` : '-';
  };

  const getDefaultPrice = (productId) => {
    const p = products.find(p => p.id === productId);
    return p ? p.default_sale_price : 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/customer-prices/', {
        customer_id: parseInt(id),
        product_id: parseInt(form.product_id),
        sale_price: parseFloat(form.sale_price),
        currency: form.currency
      });
      setShowForm(false);
      setForm({ product_id: '', sale_price: '', currency: 'USD' });
      fetchPrices();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleDelete = async (priceId) => {
    if (!window.confirm('Bu özel fiyatı silmek istediğinize emin misiniz?')) return;
    await api.delete(`/api/customer-prices/${priceId}`);
    fetchPrices();
  };

  const handleProductSelect = (productId) => {
    const defaultPrice = getDefaultPrice(parseInt(productId));
    setForm({ ...form, product_id: productId, sale_price: defaultPrice });
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/customers')} className="text-gray-500 hover:text-gray-700">← Geri</button>
        <h2 className="text-2xl font-bold text-gray-800">
          Özel Fiyatlar — {customer?.company_name}
        </h2>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-700">
        Bu müşteri için tanımlanan özel fiyatlar, teklif oluştururken varsayılan fiyatların yerine otomatik önerilir.
      </div>

      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
        >
          + Özel Fiyat Ekle
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Özel Fiyat Tanımla</h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Ürün/Hizmet *</label>
                <select
                  required
                  value={form.product_id}
                  onChange={e => handleProductSelect(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Seçiniz</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.code} - {p.name} (Varsayılan: {p.default_sale_price} {p.currency})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Özel Satış Fiyatı *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={form.sale_price}
                  onChange={e => setForm({ ...form, sale_price: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Para Birimi</label>
                <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="w-full border rounded px-3 py-2">
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="TRY">TRY</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Kaydet</button>
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Ürün/Hizmet</th>
              <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Varsayılan Fiyat</th>
              <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Özel Fiyat</th>
              <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Fark</th>
              <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {prices.map(p => {
              const defaultPrice = getDefaultPrice(p.product_id);
              const diff = p.sale_price - defaultPrice;
              return (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">{getProductName(p.product_id)}</td>
                  <td className="px-6 py-4 text-right text-gray-500">{defaultPrice.toFixed(2)} {p.currency}</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-800">{p.sale_price.toFixed(2)} {p.currency}</td>
                  <td className="px-6 py-4 text-right">
                    <span className={diff < 0 ? 'text-red-500 font-medium' : 'text-green-600 font-medium'}>
                      {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700 font-medium text-sm">Sil</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {prices.length === 0 && (
          <div className="text-center py-12 text-gray-400">Bu müşteri için henüz özel fiyat tanımlanmamış</div>
        )}
      </div>
    </div>
  );
}
